import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { UserAvatar } from "../components/UserAvatar";
import { HalfStarRating } from "../components/HalfStarRating";
import { UserCard } from "../components/UserCard";
import { Rss, Search, Users, Loader2, BookOpen, Film, Tv, Gamepad2, Send } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { useTranslation } from "../i18n";

const API = '/api';

const mediaIcons = { book: BookOpen, movie: Film, tv_series: Tv, video_game: Gamepad2 };

function timeAgo(dateStr, t) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return t.feed.timeAgo.now;
  if (diff < 3600) return t.feed.timeAgo.minutes(Math.floor(diff / 60));
  if (diff < 86400) return t.feed.timeAgo.hours(Math.floor(diff / 3600));
  return t.feed.timeAgo.days(Math.floor(diff / 86400));
}

function ActivityCard({ activity, index }) {
  const { t } = useTranslation();
  const Icon = mediaIcons[activity.item_media_type] || BookOpen;
  const actionLabel = t.feed.actions[activity.action] || activity.action;
  const ratingExtra = activity.extra?.rating ? ` ${t.feed.ratedWith(activity.extra.rating)}` : "";

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: index * 0.04 }}>
      <Card className="overflow-hidden" data-testid={`activity-${activity.activity_id}`}>
        <CardContent className="p-3 md:p-4">
          <div className="flex gap-3">
            <Link to={`/profile/${activity.user?.username}`} className="flex-shrink-0">
              <UserAvatar user={activity.user} size="md" />
            </Link>
            <div className="flex-1 min-w-0">
              <p className="text-sm leading-snug">
                <Link to={`/profile/${activity.user?.username}`} className="font-semibold hover:underline">
                  {activity.user?.username}
                </Link>{" "}
                <span className="text-muted-foreground">{actionLabel}</span>{" "}
                {activity.item_id ? (
                  <Link to={`/media/${activity.item_id}`} className="font-medium text-primary hover:underline">
                    {activity.item_title}
                  </Link>
                ) : (
                  <span className="font-medium">{activity.item_title}</span>
                )}
                {ratingExtra && <span className="text-muted-foreground">{ratingExtra}</span>}
              </p>
              <div className="flex items-center gap-2 mt-1.5">
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  <Icon className="w-2.5 h-2.5 mr-0.5" />
                  {t.mediaTypes[activity.item_media_type] || activity.item_media_type}
                </Badge>
                <span className="text-[10px] text-muted-foreground">{timeAgo(activity.created_at, t)}</span>
              </div>
            </div>
            {activity.item_cover && (
              <Link to={`/media/${activity.item_id}`} className="flex-shrink-0 hidden sm:block">
                <img src={activity.item_cover} alt="" className="w-10 h-14 rounded object-cover border border-border" />
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function RecommendationCard({ rec, index }) {
  const { t } = useTranslation();
  const Icon = mediaIcons[rec.item?.media_type] || BookOpen;

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: index * 0.04 }}>
      <Card className="overflow-hidden" data-testid={`recommendation-${rec.recommendation_id}`}>
        <CardContent className="p-3 md:p-4">
          <div className="flex gap-3">
            <Link to={`/profile/${rec.sender?.username}`} className="flex-shrink-0">
              <UserAvatar user={rec.sender} size="md" />
            </Link>
            <div className="flex-1 min-w-0">
              <p className="text-sm leading-snug">
                <Link to={`/profile/${rec.sender?.username}`} className="font-semibold hover:underline">
                  {rec.sender?.username}
                </Link>{" "}
                <span className="text-muted-foreground">{t.feed.recommendedYou}</span>{" "}
                {rec.item ? (
                  <Link to={`/media/${rec.item.item_id}`} className="font-medium text-primary hover:underline">
                    {rec.item.title}
                  </Link>
                ) : (
                  <span className="font-medium">{t.feed.aTitle}</span>
                )}
              </p>
              <div className="flex items-center gap-2 mt-1.5">
                {rec.item && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    <Icon className="w-2.5 h-2.5 mr-0.5" />
                    {t.mediaTypes[rec.item.media_type] || rec.item.media_type}
                  </Badge>
                )}
                <span className="text-[10px] text-muted-foreground">{timeAgo(rec.created_at, t)}</span>
              </div>
            </div>
            {rec.item?.cover_url && (
              <Link to={`/media/${rec.item.item_id}`} className="flex-shrink-0 hidden sm:block">
                <img src={rec.item.cover_url} alt="" className="w-10 h-14 rounded object-cover border border-border" />
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function Feed({ user }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("feed");
  const [activities, setActivities] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const limit = 30;

  useEffect(() => { fetchFeed(0); }, []);

  useEffect(() => {
    if (tab === "recommendations" && recommendations.length === 0) {
      fetchRecommendations(0);
    }
  }, [tab]);

  const fetchFeed = async (offset) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/feed?limit=${limit}&skip=${offset}`);
      if (offset === 0) setActivities(res.data.activities);
      else setActivities(prev => [...prev, ...res.data.activities]);
      setHasMore(res.data.has_more);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchRecommendations = async (offset) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/recommendations?limit=${limit}&skip=${offset}`);
      if (offset === 0) setRecommendations(res.data.recommendations);
      else setRecommendations(prev => [...prev, ...res.data.recommendations]);
      setHasMore(res.data.has_more);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadMore = () => {
    const next = skip + limit;
    setSkip(next);
    if (tab === "feed") fetchFeed(next);
    else fetchRecommendations(next);
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setSkip(0);
    setHasMore(false);
  };

  const handleSearch = useCallback(async (q) => {
    setSearchQuery(q);
    if (q.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/users/search/find?q=${encodeURIComponent(q.trim())}`);
      setSearchResults(res.data.users || []);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  }, []);

  const tabs = [
    { id: "feed", label: t.feed.tabFeed },
    { id: "recommendations", label: t.feed.tabRecommendations },
  ];

  const currentData = tab === "feed" ? activities : recommendations;
  const isLoading = loading && currentData.length === 0;

  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="feed-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-3">
            <Rss className="w-7 h-7 text-primary" />
            {t.feed.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t.feed.subtitle}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowSearch(!showSearch)} data-testid="toggle-user-search">
          <Search className="w-4 h-4 mr-1.5" />
          {t.userSearch.title}
        </Button>
      </div>

      {/* Toggle Tabs */}
      <div className="flex bg-muted rounded-lg p-1" data-testid="feed-tabs">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            onClick={() => handleTabChange(tb.id)}
            data-testid={`feed-tab-${tb.id}`}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
              tab === tb.id
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {showSearch && (
        <Card data-testid="user-search-section">
          <CardContent className="p-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t.userSearch.placeholder}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9 min-h-[44px]"
                data-testid="user-search-input"
              />
            </div>
            {searching && <div className="flex justify-center py-2"><Loader2 className="w-4 h-4 animate-spin" /></div>}
            {searchResults.length > 0 && (
              <div className="space-y-2">
                {searchResults.map(u => (<UserCard key={u.user_id} user={u} />))}
              </div>
            )}
            {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-3">{t.userSearch.noResults}</p>
            )}
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : currentData.length === 0 ? (
        <Card className="text-center py-12" data-testid={`${tab}-empty`}>
          <CardContent>
            {tab === "feed" ? (
              <>
                <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">{t.feed.empty}</h3>
                <p className="text-sm text-muted-foreground mb-6">{t.feed.emptyDesc}</p>
                <Button onClick={() => setShowSearch(true)} data-testid="find-people-btn">
                  <Search className="w-4 h-4 mr-2" /> {t.feed.findPeople}
                </Button>
              </>
            ) : (
              <>
                <Send className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">{t.feed.noRecommendations}</h3>
                <p className="text-sm text-muted-foreground">{t.feed.noRecommendationsDesc}</p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid={`${tab}-list`}>
          {tab === "feed"
            ? activities.map((act, i) => (<ActivityCard key={act.activity_id} activity={act} index={i} />))
            : recommendations.map((rec, i) => (<RecommendationCard key={rec.recommendation_id} rec={rec} index={i} />))
          }
          {hasMore && (
            <Button variant="outline" className="w-full" onClick={loadMore} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t.social.loadMore}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default Feed;
